import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileUp, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const ESITI = { aggiunto: "pagamento aggiunto", convertito: "pagamento convertito in bonifico parlante", gia_presente: "già registrato, saltato", errore: "errore" };

const Riga = ({ r, i, fatture, onChange }) => {
  const ids = new Set(r.candidati.map((c) => c.id));
  return (
    <div className="rounded-md border border-border p-3 space-y-2 text-sm" data-testid={`cert-riga-${i}`}>
      <div className="flex flex-wrap justify-between gap-2 min-w-0">
        <div className="min-w-0 truncate"><strong>{r.data || "—"}</strong> · {r.ordinante || "ordinante non letto"}</div>
        <div className="tabular-nums">Bonifico <strong>{fmt(r.importo)}</strong></div>
      </div>
      {r.causale && <div className="text-[11px] text-muted-foreground truncate" title={r.causale}>Causale: {r.causale}</div>}
      <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-3 gap-2">
        <div className="min-w-0">
          <Label className="text-xs">Ritenuta €</Label>
          <Input data-testid={`cert-ritenuta-${i}`} type="number" step="0.01" value={r.ritenuta} onChange={(e) => onChange({ ...r, ritenuta: e.target.value })} className="h-9" />
        </div>
        <div className="sm:col-span-2 min-w-0">
          <Label className="text-xs">Fattura {r.fattura_id && r.candidati[0]?.id === r.fattura_id && <span className="text-primary">· riconosciuta ({Math.round(r.candidati[0].punteggio * 100)}%)</span>}</Label>
          <select data-testid={`cert-fattura-${i}`} value={r.fattura_id} onChange={(e) => onChange({ ...r, fattura_id: e.target.value })} className={`w-full max-w-full min-w-0 h-9 rounded-md border bg-background px-2 text-sm ${r.fattura_id ? "border-input" : "border-amber-500"}`}>
            <option value="">— salta questa riga —</option>
            {r.candidati.length > 0 && <optgroup label="Proposte">{r.candidati.map((c) => <option key={c.id} value={c.id}>n. {c.numero} · {c.cliente} · residuo {fmt(c.residuo)} ({Math.round(c.punteggio * 100)}%)</option>)}</optgroup>}
            <optgroup label="Tutte le fatture di vendita">{fatture.filter((f) => !ids.has(f.id)).map((f) => <option key={f.id} value={f.id}>n. {f.numero} · {f.cliente} · {f.data} · residuo {fmt(f.residuo)}</option>)}</optgroup>
          </select>
        </div>
      </div>
      {!r.fattura_id && <div className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Fattura non riconosciuta: scegline una dall'elenco.</div>}
    </div>
  );
};

export const CertificazioneImport = ({ onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState(null);
  const [esiti, setEsiti] = useState(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setEsiti(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/crediti/certificazione", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      if (!data.righe.length) toast.error("Nessun bonifico con ritenuta trovato nel documento");
      else setD(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore lettura certificazione"); } finally { setBusy(false); }
  };
  const conferma = async () => {
    const righe = d.righe.filter((r) => r.fattura_id).map((r) => ({ fattura_id: r.fattura_id, data: r.data, importo: r.importo, ritenuta: num(r.ritenuta) }));
    if (!righe.length) return toast.error("Collega almeno una riga a una fattura");
    setBusy(true);
    try {
      const { data } = await api.post("/crediti/certificazione/conferma", { certificazione_id: d.certificazione_id, righe });
      setEsiti(data.esiti); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const setRiga = (i, r) => setD({ ...d, righe: d.righe.map((x, j) => (j === i ? r : x)) });
  const totRit = d ? d.righe.filter((r) => r.fattura_id).reduce((s, r) => s + num(r.ritenuta), 0) : 0;

  return (
    <>
      <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} data-testid="cert-file-input" />
      <Button variant="outline" className="h-10" disabled={busy} onClick={() => ref.current?.click()} data-testid="cert-upload-btn">
        {busy && !d ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}{busy && !d ? "Lettura in corso…" : "Carica certificazione"}
      </Button>
      <Dialog open={!!d} onOpenChange={(o) => { if (!o) { setD(null); setEsiti(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="cert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Certificazione ritenute</DialogTitle>
            <DialogDescription>{d?.filename} · {d?.righe.length} bonifici letti. Controlla fattura e ritenuta, poi conferma.</DialogDescription>
          </DialogHeader>
          {d && !esiti && (
            <div className="space-y-3">
              {d.righe.map((r, i) => <Riga key={i} r={r} i={i} fatture={d.fatture} onChange={(x) => setRiga(i, x)} />)}
              <div className="text-sm flex justify-between"><span>Credito da aggiungere</span><strong className="text-primary tabular-nums" data-testid="cert-totale">{fmt(totRit)}</strong></div>
              <Button data-testid="cert-conferma" onClick={conferma} disabled={busy} className="w-full h-11">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Registra sulle fatture</Button>
            </div>
          )}
          {esiti && (
            <div className="space-y-2" data-testid="cert-esiti">
              {esiti.map((e, i) => (
                <div key={i} className={`text-sm flex items-center gap-2 ${e.esito === "errore" ? "text-destructive" : ""}`}>
                  {e.esito === "errore" ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  Fattura n. {e.numero || "—"}: {ESITI[e.esito]}{e.errore ? ` (${e.errore})` : ""}
                </div>
              ))}
              <Button variant="outline" className="w-full" onClick={() => { setD(null); setEsiti(null); }} data-testid="cert-chiudi">Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
